import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { friendlyErrorMessage } from "@/platform/lib/error-messages";
import { ArrowUpRight, Download, Loader2, Search, Shield } from "lucide-react";

import { AdminShell } from "@/platform/layout/admin-shell";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Input } from "@/platform/ui/input";
import { listAdminUsers, type AdminUserRow } from "@/platform/admin/api";
import { cn } from "@/platform/lib/cn";
import { downloadCsv } from "@/platform/lib/csv";

type TenantFilter = "all" | "einstein" | "usp";
type StatusFilter = "all" | "active" | "trialing" | "canceled" | "none";

export function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [tenant, setTenant] = useState<TenantFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const query = useQuery({
    queryKey: ["admin", "users", { search, tenant, status, page, pageSize }],
    queryFn: () =>
      listAdminUsers({
        search: search.trim() || undefined,
        tenant,
        status,
        page,
        pageSize,
      }),
    staleTime: 30_000,
  });

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / pageSize)) : 1;
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      const all = await listAdminUsers({
        search: search.trim() || undefined,
        tenant,
        status,
        page: 1,
        pageSize: 1000,
      });
      if (all.users.length === 0) {
        toast.info("Sem usuárias para exportar com os filtros atuais.");
        return;
      }
      downloadCsv(
        `usuarios_${new Date().toISOString().slice(0, 10)}.csv`,
        all.users,
        [
          { key: "email", header: "Email" },
          { key: (u) => u.full_name ?? u.display_name ?? "", header: "Nome" },
          { key: (u) => (u.is_admin ? "sim" : "não"), header: "Admin" },
          {
            key: (u) =>
              u.subscriptions
                .filter((s) => s.status === "active" || s.status === "trialing")
                .map((s) => `${s.tenant}/${s.plan}/${s.status}`)
                .join(" | "),
            header: "Assinaturas ativas",
          },
          {
            key: (u) => new Date(u.created_at).toLocaleDateString("pt-BR"),
            header: "Cadastro",
          },
          {
            key: (u) =>
              u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString("pt-BR") : "",
            header: "Último login",
          },
          { key: "user_id", header: "user_id" },
        ],
      );
      toast.success(`Exportadas ${all.users.length} usuárias.`);
    } catch (err) {
      toast.error(friendlyErrorMessage(err, "Falha ao exportar."));
    } finally {
      setExporting(false);
    }
  }

  return (
    <AdminShell
      title="Usuárias"
      description="Liste, filtre e gerencie as contas da plataforma. Clique numa linha para abrir o detalhe."
      actions={
        <Button variant="outline" size="sm" onClick={handleExport} loading={exporting}>
          <Download className="size-3.5" />
          Exportar CSV
        </Button>
      }
    >
      {/* Filtros */}
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div>
          <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            Buscar
          </label>
          <div className="mt-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[color:var(--color-muted-foreground)]" aria-hidden />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="email ou nome"
              className="pl-9"
            />
          </div>
        </div>
        <FilterSelect
          label="Tenant"
          value={tenant}
          onChange={(v) => {
            setTenant(v as TenantFilter);
            setPage(1);
          }}
          options={[
            { value: "all", label: "Todos" },
            { value: "einstein", label: "Einstein" },
            { value: "usp", label: "USP" },
          ]}
        />
        <FilterSelect
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v as StatusFilter);
            setPage(1);
          }}
          options={[
            { value: "all", label: "Todos" },
            { value: "active", label: "Ativa" },
            { value: "trialing", label: "Em trial" },
            { value: "canceled", label: "Cancelada" },
            { value: "none", label: "Sem assinatura" },
          ]}
        />
      </div>

      {/* Tabela */}
      <div className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] overflow-hidden">
        {query.isLoading ? (
          <div className="p-10 flex items-center justify-center">
            <Loader2 className="size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
          </div>
        ) : query.error ? (
          <p className="p-10 text-[color:var(--color-danger)] text-[length:var(--text-body-sm)]">
            Falha ao carregar usuárias.
          </p>
        ) : !query.data || query.data.users.length === 0 ? (
          <p className="p-10 text-center text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            Nenhuma usuária encontrada com esses filtros.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[length:var(--text-body-sm)]">
                <thead className="bg-[color:var(--color-muted)]/50">
                  <tr>
                    <Th>Usuária</Th>
                    <Th>Assinaturas</Th>
                    <Th>Cadastro</Th>
                    <Th>Último login</Th>
                    <Th className="text-right">Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.users.map((u) => (
                    <UserRow key={u.user_id} user={u} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-[--color-border] px-4 py-3 text-[length:var(--text-body-sm)]">
              <span className="text-[color:var(--color-muted-foreground)]">
                {query.data.total.toLocaleString("pt-BR")} usuárias · página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function UserRow({ user }: { user: AdminUserRow }) {
  const activeSubs = user.subscriptions.filter(
    (s) => s.status === "active" || s.status === "trialing",
  );
  const isBanned = user.banned_until && new Date(user.banned_until) > new Date();

  return (
    <tr className="border-t border-[--color-border]">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium truncate">
                {user.full_name ?? user.display_name ?? user.email}
              </span>
              {user.is_admin && (
                <Badge variant="outline" className="gap-1">
                  <Shield className="size-3" aria-hidden />
                  Admin
                </Badge>
              )}
              {isBanned && (
                <Badge variant="outline" className="text-[color:var(--color-danger)] border-[color:var(--color-danger)]">
                  Banida
                </Badge>
              )}
              {!user.email_confirmed_at && (
                <Badge variant="outline">Não confirmado</Badge>
              )}
            </div>
            <p className="text-[color:var(--color-muted-foreground)] truncate">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {activeSubs.length === 0 ? (
          <span className="text-[color:var(--color-muted-foreground)]">—</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {activeSubs.map((s, i) => (
              <Badge key={i} variant="outline" className="capitalize">
                {s.tenant} · {s.plan} · {s.status}
              </Badge>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-[color:var(--color-muted-foreground)]">
        {new Date(user.created_at).toLocaleDateString("pt-BR")}
      </td>
      <td className="px-4 py-3 text-[color:var(--color-muted-foreground)]">
        {user.last_sign_in_at
          ? new Date(user.last_sign_in_at).toLocaleDateString("pt-BR")
          : "—"}
      </td>
      <td className="px-4 py-3 text-right">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/admin/usuarios/${user.user_id}`}>
            Detalhe
            <ArrowUpRight className="size-3.5" aria-hidden />
          </Link>
        </Button>
      </td>
    </tr>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "px-4 py-3 text-left text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)] font-medium",
        className,
      )}
    >
      {children}
    </th>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div>
      <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full sm:w-auto rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3.5 py-2.5 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
